import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Cloud, 
  RefreshCw, 
  Shield, 
  CreditCard, 
  ArrowRight, 
  Check,
  Building2,
  ShoppingCart,
  Zap,
  Users,
  BarChart3,
  Lock,
  Menu,
  X
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLUGIN_PRICE = 289.00;

export default function LandingPage() {
  const [showCheckout, setShowCheckout] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    company: ""
  });

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    try {
      const response = await axios.post(`${API}/coupon/validate`, { code: couponCode });
      if (response.data.valid) {
        setCouponApplied(response.data);
        toast.success("Coupon applied successfully!");
      } else {
        toast.error(response.data.message || "Invalid coupon code");
        setCouponApplied(null);
      }
    } catch (error) {
      toast.error("Failed to validate coupon");
      setCouponApplied(null);
    }
  };

  const handleCheckout = async () => {
    if (!formData.email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        email: formData.email,
        name: formData.name,
        company: formData.company,
        coupon_code: couponApplied ? couponCode : null,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <RefreshCw className="w-8 h-8" />,
      title: "Two-Way Sync",
      description: "Real-time synchronization between Cloudbeds PMS and your WooCommerce store. Rooms, rates, and availability always in sync."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Secure Payments",
      description: "Support for Scotiabank, Bank of the Bahamas, and Visa payment processors with PCI-compliant transactions."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Guest Management",
      description: "Automatically sync guest information between systems. No more manual data entry or missed reservations."
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Analytics Dashboard",
      description: "Track bookings, revenue, and sync status from your WordPress admin panel."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Instant Updates",
      description: "Room availability updates within seconds. Never oversell or miss a booking opportunity."
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Enterprise Security",
      description: "Bank-grade encryption and secure API connections protect your business data."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="CloudSync Pro" className="h-14 object-contain" />
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="nav-link text-slate-600 hover:text-midnight">Features</a>
              <a href="#pricing" className="nav-link text-slate-600 hover:text-midnight">Pricing</a>
              <a href="#faq" className="nav-link text-slate-600 hover:text-midnight">FAQ</a>
              <a href="/dashboard" className="nav-link text-slate-600 hover:text-midnight">My Account</a>
              <Button 
                data-testid="nav-get-started-btn"
                onClick={() => setShowCheckout(true)}
                className="bg-midnight text-white hover:bg-slate-800 rounded-full px-6"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4 px-6">
            <div className="flex flex-col gap-4">
              <a href="#features" className="text-slate-600 hover:text-midnight">Features</a>
              <a href="#pricing" className="text-slate-600 hover:text-midnight">Pricing</a>
              <a href="#faq" className="text-slate-600 hover:text-midnight">FAQ</a>
              <a href="/dashboard" className="text-slate-600 hover:text-midnight">My Account</a>
              <Button 
                onClick={() => { setShowCheckout(true); setMobileMenuOpen(false); }}
                className="bg-midnight text-white hover:bg-slate-800 rounded-full"
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section 
        className="hero-section relative min-h-screen flex items-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1758194090785-8e09b7288199?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMGxvYmJ5JTIwaW50ZXJpb3IlMjBtb2Rlcm58ZW58MHx8fHwxNzY4MDA2Njg1fDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 text-white">
              <Badge className="bg-bronze/20 text-bronze border-bronze/30 mb-6 animate-fade-in">
                WordPress Plugin for Hotels
              </Badge>
              
              <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl font-bold leading-none mb-6 animate-fade-in-up">
                Connect Cloudbeds
                <br />
                <span className="gradient-text">to WooCommerce</span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-xl animate-fade-in-up animation-delay-200">
                Seamless two-way synchronization for your hotel's room inventory, 
                pricing, and bookings. Stop manual updates. Start growing.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
                <Button 
                  data-testid="hero-get-started-btn"
                  onClick={() => setShowCheckout(true)}
                  className="bg-bronze hover:bg-amber-600 text-white rounded-full px-8 py-6 text-lg font-medium shadow-glow"
                >
                  Get CloudSync Pro
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20 rounded-full px-8 py-6 text-lg"
                  onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                >
                  Learn More
                </Button>
              </div>

              <div className="flex items-center gap-8 mt-12 text-sm text-slate-400 animate-fade-in-up animation-delay-400">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>One-time purchase</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>Lifetime updates</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>30-day refund</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 hidden lg:block">
              <div className="glass rounded-2xl p-6 animate-slide-in-right">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="space-y-3 font-mono text-sm">
                  <div className="flex items-center gap-2 text-emerald-600">
                    <Check className="w-4 h-4" />
                    <span>Room synced: Deluxe Suite</span>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-600">
                    <Check className="w-4 h-4" />
                    <span>Price updated: $299/night</span>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-600">
                    <Check className="w-4 h-4" />
                    <span>Booking confirmed: #WC-1234</span>
                  </div>
                  <div className="flex items-center gap-2 text-bronze">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Syncing availability...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logos Section */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-center text-slate-500 mb-8">Trusted integration partners</p>
          <div className="flex flex-wrap justify-center items-center gap-12">
            <div className="flex items-center gap-2">
              <Building2 className="w-8 h-8" />
              <span className="text-xl font-semibold">Cloudbeds</span>
            </div>
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-8 h-8" />
              <span className="text-xl font-semibold">WooCommerce</span>
            </div>
            <div className="flex items-center">
              <img 
                src="/images/scotiabank-logo.png" 
                alt="Scotiabank" 
                className="h-12 object-contain"
              />
            </div>
            <div className="flex items-center">
              <img 
                src="/images/bob-logo.png" 
                alt="Bank of the Bahamas" 
                className="h-12 object-contain"
              />
            </div>
            <div className="flex items-center">
              <img 
                src="/images/visa-logo.png" 
                alt="Visa" 
                className="h-10 object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <Badge className="bg-bronze/10 text-bronze border-bronze/20 mb-4">Features</Badge>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-midnight mb-4">
              Everything You Need
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A complete integration solution designed for hospitality professionals
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="feature-card border-slate-200/50 card-hover"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="pt-6">
                  <div className="w-14 h-14 rounded-xl bg-bronze/10 flex items-center justify-center text-bronze mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-midnight mb-2 font-outfit">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <Badge className="bg-bronze/10 text-bronze border-bronze/20 mb-4">Pricing</Badge>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-midnight mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-slate-600">
              One-time payment. Lifetime access. No subscriptions.
            </p>
          </div>

          <div className="max-w-lg mx-auto">
            <Card className="pricing-card shadow-elevated" data-testid="pricing-card">
              <CardHeader className="text-center pb-4 pt-8">
                <Badge className="w-fit mx-auto mb-4 bg-bronze text-white">Most Popular</Badge>
                <CardTitle className="text-2xl font-outfit">CloudSync Pro</CardTitle>
                <CardDescription>Everything you need to sync Cloudbeds with WooCommerce</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-8">
                  <span className="text-6xl font-bold text-midnight font-outfit">$289</span>
                  <span className="text-slate-500 ml-2">one-time</span>
                </div>

                <ul className="text-left space-y-4 mb-8">
                  {[
                    "Two-way room & rate sync",
                    "Real-time availability updates",
                    "Guest information sync",
                    "Scotiabank, BOB & Visa support",
                    "WordPress admin dashboard",
                    "Lifetime updates",
                    "Priority email support",
                    "30-day money-back guarantee"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  data-testid="pricing-get-started-btn"
                  onClick={() => setShowCheckout(true)}
                  className="w-full bg-midnight hover:bg-slate-800 text-white rounded-full py-6 text-lg font-medium"
                >
                  Get CloudSync Pro
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                
                <p className="text-sm text-slate-500 mt-4">
                  Includes 2% transaction fee on bookings
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <Badge className="bg-bronze/10 text-bronze border-bronze/20 mb-4">FAQ</Badge>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-midnight mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: "What is the 2% transaction fee?",
                a: "We charge a small 2% fee on each booking processed through the plugin. This helps us maintain the infrastructure and provide ongoing support and updates."
              },
              {
                q: "How will the payment appear on my statement?",
                a: "All payments will be processed by Sebastian Alliance Group. This is the name that will appear on your credit card or bank statement."
              },
              {
                q: "Do I need a Cloudbeds account?",
                a: "Yes, you need an active Cloudbeds account with API access. Contact Cloudbeds to enable API access for your property."
              },
              {
                q: "What payment processors are supported?",
                a: "CloudSync Pro supports Scotiabank, Bank of the Bahamas, and Visa payment processors. More processors may be added in future updates."
              },
              {
                q: "Is there a refund policy?",
                a: "Yes! We offer a 30-day money-back guarantee. If CloudSync Pro doesn't meet your needs, contact us for a full refund."
              },
              {
                q: "Do I need technical skills to install?",
                a: "Basic WordPress knowledge is helpful. The plugin installs like any other WordPress plugin - upload, activate, and configure your API credentials."
              }
            ].map((faq, index) => (
              <Card key={index} className="border-slate-200">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold text-midnight mb-2 font-outfit">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-midnight">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Streamline Your Hotel Bookings?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Join hundreds of hotels using CloudSync Pro to automate their booking workflow.
          </p>
          <Button 
            data-testid="cta-get-started-btn"
            onClick={() => setShowCheckout(true)}
            className="bg-bronze hover:bg-amber-600 text-white rounded-full px-10 py-6 text-lg font-medium shadow-glow"
          >
            Get CloudSync Pro - $289
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="CloudSync Pro" className="h-12 object-contain" />
            </div>
            <div className="flex gap-6 text-sm">
              <a href="/privacy" className="hover:text-white">Privacy Policy</a>
              <a href="/terms" className="hover:text-white">Terms of Service</a>
              <a href="mailto:support@cloudsync.pro" className="hover:text-white">Support</a>
            </div>
            <p className="text-sm">© 2025 Sebastian Alliance Group. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-outfit">Get CloudSync Pro</DialogTitle>
            <DialogDescription>
              Complete your purchase to download the plugin
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input 
                id="email"
                type="email"
                data-testid="checkout-email-input"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input 
                id="name"
                data-testid="checkout-name-input"
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">Company / Hotel Name</Label>
              <Input 
                id="company"
                data-testid="checkout-company-input"
                placeholder="Grand Hotel"
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="coupon">Coupon Code</Label>
              <div className="flex gap-2">
                <Input 
                  id="coupon"
                  data-testid="checkout-coupon-input"
                  placeholder="SAVE20"
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value); setCouponApplied(null); }}
                />
                <Button 
                  type="button" 
                  variant="outline"
                  data-testid="apply-coupon-btn"
                  onClick={validateCoupon}
                >
                  Apply
                </Button>
              </div>
              {couponApplied && (
                <p className="text-sm text-emerald-600">
                  Coupon applied! You save ${couponApplied.discount_amount.toFixed(2)}
                </p>
              )}
            </div>

            <div className="border-t border-slate-200 pt-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-slate-600">CloudSync Pro Plugin</span>
                <span>${PLUGIN_PRICE.toFixed(2)}</span>
              </div>
              {couponApplied && (
                <div className="flex justify-between items-center mb-2 text-emerald-600">
                  <span>Discount</span>
                  <span>-${couponApplied.discount_amount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-lg font-semibold">
                <span>Total</span>
                <span>${couponApplied ? couponApplied.final_price.toFixed(2) : PLUGIN_PRICE.toFixed(2)}</span>
              </div>
            </div>

            <Button 
              data-testid="proceed-to-payment-btn"
              onClick={handleCheckout}
              disabled={loading}
              className="w-full bg-midnight hover:bg-slate-800 text-white rounded-full py-6 text-lg"
            >
              {loading ? (
                <>
                  <div className="spinner w-5 h-5 mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  Proceed to Payment
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>

            <p className="text-xs text-center text-slate-500">
              Secure payment powered by Stripe. Your card details are never stored on our servers.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
